import { useState, useEffect } from 'react';
import axios from 'axios';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';

interface Stat {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  icon: string;
  color: string;
}

export default function DashboardStats() {
  const [stats, setStats] = useState<Stat[]>([]);
  const [loading, setLoading] = useState(true);

  // Formatage monétaire en FCFA
  const formatFCFA = (value: number): string => {
    return value.toLocaleString('fr-CM', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + ' FCFA';
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getResources('dashboard/stats');
        
        if (response.data.success) {
          const data = response.data.data;

          setStats([
            {
              title: 'Chiffre d\'Affaires du Mois',
              value: formatFCFA(data.monthlyRevenue),
              change: data.monthlyRevenueChange > 0 ? `+${data.monthlyRevenueChange}%` : `${data.monthlyRevenueChange}%`,
              changeType: data.monthlyRevenueChange >= 0 ? 'positive' : 'negative',
              icon: 'ri-money-dollar-circle-fill',
              color: 'bg-green-500'
            },
            {
              title: 'Commandes en Cours',
              value: data.pendingOrders.toString(),
              change: data.pendingOrdersChange >= 0 ? `+${data.pendingOrdersChange}` : data.pendingOrdersChange.toString(),
              changeType: data.pendingOrdersChange >= 0 ? 'positive' : 'negative',
              icon: 'ri-shopping-cart-fill',
              color: 'bg-blue-500'
            },
            {
              title: 'Stock Total',
              value: `${data.totalStockArticles} articles`,
              change: data.stockChange >= 0 ? `+${data.stockChange}%` : `${data.stockChange}%`,
              changeType: data.stockChange >= 0 ? 'positive' : 'negative',
              icon: 'ri-archive-fill',
              color: 'bg-orange-500'
            },
            {
              title: 'Créances Clients',
              value: formatFCFA(data.clientReceivables),
              change: data.receivablesChange >= 0 ? `+${data.receivablesChange}%` : `${data.receivablesChange}%`,
              changeType: data.receivablesChange >= 0 ? 'positive' : 'negative',
              icon: 'ri-bank-card-fill',
              color: 'bg-red-500'
            },
            {
              title: 'Fournisseurs Actifs',
              value: data.activeSuppliers.toString(),
              change: data.suppliersChange >= 0 ? `+${data.suppliersChange}` : data.suppliersChange.toString(),
              changeType: data.suppliersChange >= 0 ? 'positive' : 'negative',
              icon: 'ri-truck-fill',
              color: 'bg-purple-500'
            },
            {
              title: 'Marge Brute',
              value: `${data.grossMargin}%`,
              change: data.marginChange >= 0 ? `+${data.marginChange}%` : `${data.marginChange}%`,
              changeType: data.marginChange >= 0 ? 'positive' : 'negative',
              icon: 'ri-line-chart-fill',
              color: 'bg-teal-500'
            }
          ]);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques', error);
        // Fallback avec données statiques en cas d'erreur
        setStats([
          { title: 'Chiffre d\'Affaires du Mois', value: '2 450 000 FCFA', change: '+12.5%', changeType: 'positive', icon: 'ri-money-dollar-circle-fill', color: 'bg-green-500' },
          { title: 'Commandes en Cours', value: '47', change: '+3', changeType: 'positive', icon: 'ri-shopping-cart-fill', color: 'bg-blue-500' },
          { title: 'Stock Total', value: '1 234 articles', change: '-2.1%', changeType: 'negative', icon: 'ri-archive-fill', color: 'bg-orange-500' },
          { title: 'Créances Clients', value: '890 000 FCFA', change: '+5.8%', changeType: 'negative', icon: 'ri-bank-card-fill', color: 'bg-red-500' },
          { title: 'Fournisseurs Actifs', value: '23', change: '+2', changeType: 'positive', icon: 'ri-truck-fill', color: 'bg-purple-500' },
          { title: 'Marge Brute', value: '18.5%', change: '+1.2%', changeType: 'positive', icon: 'ri-line-chart-fill', color: 'bg-teal-500' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-24 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
              </div>
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-300"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
              <p className="text-2xl font-bold text-gray-900 mb-2">{stat.value}</p>
              <div className="flex items-center space-x-1">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500">vs mois dernier</span>
              </div>
            </div>
            <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
              <i className={`${stat.icon} text-white text-xl`}></i>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}